import { useMemo, useState } from 'react'
import type { ComponentType } from 'react'
import AppLayout from '../AppLayout'
import BSDCard from '../BSDCard'
import AutoGrid from '../AutoGrid'
import DistrictMap from '../DistrictMap'
import type { StrutturaDisabilita } from '../../data/struttureDisabilita'
import { DisabilitaDialog } from '../StrutturaDetailDialog'
import type { AmbitoId } from '../../data/ambitiStats'
import type { SezioneCard } from '../SectionBadge'
import { AlertTriangle, BedDouble, CheckCircle, Clock, Layers, List, TrendingUp } from 'lucide-react'
import DisabilitaDialogList from './DisabilitaDialogList'

interface StrutturaDisabilitaPageProps {
  titolo: string
  sottotitolo: string
  accentColor: string
  accentColorB: string
  sezione: SezioneCard
  descrizione: React.ReactNode
  lista: StrutturaDisabilita[]
  kpiLabel: string
  icon: ComponentType<{ size?: number | string; style?: React.CSSProperties }>
  tipo: 'RSD' | 'CSS' | 'CDD'
}

export default function StrutturaDisabilitaPage(props: StrutturaDisabilitaPageProps) {
  return (
    <AppLayout>
      {({ selectedAmbito, onSelectedAmbitoChange }) => (
        <StrutturaDisabilitaContent
          {...props}
          selectedAmbito={selectedAmbito}
          onSelectedAmbitoChange={onSelectedAmbitoChange}
        />
      )}
    </AppLayout>
  )
}

function StrutturaDisabilitaContent({
  titolo,
  sottotitolo,
  accentColor,
  accentColorB,
  sezione,
  descrizione,
  lista,
  kpiLabel,
  icon: Icon,
  tipo,
  selectedAmbito,
  onSelectedAmbitoChange,
}: StrutturaDisabilitaPageProps & {
  selectedAmbito: AmbitoId | null
  onSelectedAmbitoChange: (id: AmbitoId | null) => void
}) {
  const filtered = useMemo(
    () => selectedAmbito ? lista.filter(s => s.ambitoId === selectedAmbito) : lista,
    [lista, selectedAmbito],
  )
  const [selectedStruttura, setSelectedStruttura] = useState<StrutturaDisabilita | null>(null)

  const stats = useMemo(() => {
    const totPersoneInLista = filtered.reduce((s, r) => s + (r.personeInLista ?? 0), 0)
    const totPostiContratto = filtered.reduce((s, r) => s + (r.postiContratto ?? 0), 0)
    const totPostiDisponibili = filtered.reduce((s, r) => s + (r.postiDisponibili ?? 0), 0)
    const conListaAperta = filtered.filter(r => r.listaAttesa)
    const conPostiLiberi = filtered.filter(r => (r.postiDisponibili ?? 0) > 0)
    const tassoOccupazione = totPostiContratto > 0
      ? Math.round(((totPostiContratto - totPostiDisponibili) / totPostiContratto) * 100)
      : null
    const byLista = [...filtered]
      .filter(r => r.personeInLista > 0)
      .sort((a, b) => b.personeInLista - a.personeInLista)
    return { totPersoneInLista, totPostiContratto, totPostiDisponibili, conListaAperta, conPostiLiberi, tassoOccupazione, byLista }
  }, [filtered])

  return (
    <section className="telemetry-dashboard">
      <div className="telemetry-inner">

      <h3 className="telemetry-section-title" style={{ marginBottom: 0 }}>
        {selectedAmbito
          ? `Ambito di ${selectedAmbito.charAt(0).toUpperCase() + selectedAmbito.slice(1)} - ${titolo}`
          : `Provincia di Monza e Brianza - ${titolo}`}
      </h3>

      <AutoGrid>

        {/* Card 1 — Descrizione */}
        <BSDCard kind="panel" sezione={sezione} style={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            {descrizione}
          </div>
        </BSDCard>

        {/* Card 2 — Mappa */}
        <BSDCard
          className="telemetry-map-card"
          kind="kpi"
          sezione={sezione}
          style={{ minHeight: 0, padding: 10, overflow: 'hidden' }}
        >
          <DistrictMap
            onSelectDistrict={(id) => onSelectedAmbitoChange(id as AmbitoId)}
            onClearSelection={() => onSelectedAmbitoChange(null)}
            selectedDistrictId={selectedAmbito}
          />
        </BSDCard>

        {/* Card 3 — Elenco */}
        <BSDCard
          kind="kpi"
          sezione={sezione}
          label="Elenco strutture"
          value={String(filtered.length)}
          icon={Icon}
          color={accentColor}
          zoomable
          hideZoomButton
          sub={
            <p style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.6, margin: 0 }}>
              Tutte le strutture nel territorio selezionato.
            </p>
          }
          zoomSub={<DisabilitaDialogList strutture={filtered} color={accentColor} tipo={tipo} fromUrl={`/${tipo.toLowerCase()}`} />}
          footer={(openZoom) => (
            <button
              type="button"
              onClick={openZoom}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                width: '100%',
                padding: '6px 0',
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--accent)',
                background: 'none',
                border: 'none',
                borderTop: '1px solid var(--border)',
                cursor: 'pointer',
                justifyContent: 'center',
                marginTop: 8,
                paddingTop: 8,
              }}
            >
              <List size={14} />
              Vedi elenco completo
            </button>
          )}
        />

        {/* Card 4 — Occupazione */}
        <BSDCard kind="panel" sezione={sezione} style={{ display: 'flex', flexDirection: 'column' }}>
          <h3 className="telemetry-section-title" style={{ marginTop: 0, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
            <TrendingUp size={14} style={{ color: accentColor }} />
            Occupazione
          </h3>
          {stats.tassoOccupazione !== null ? (
            <>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, marginBottom: 8 }}>
                <span style={{ fontSize: 32, fontWeight: 800, color: accentColor, lineHeight: 1 }}>
                  {stats.tassoOccupazione}%
                </span>
                <span style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 4 }}>occupato</span>
              </div>
              <div style={{ height: 8, borderRadius: 4, background: 'var(--bg-2)', overflow: 'hidden', marginBottom: 10 }}>
                <div style={{ height: '100%', width: `${stats.tassoOccupazione}%`, background: accentColor, borderRadius: 4, transition: 'width 0.4s' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-3)' }}>
                <span>{stats.totPostiContratto - stats.totPostiDisponibili} occupati</span>
                <span>{stats.totPostiDisponibili} liberi</span>
              </div>
            </>
          ) : (
            <p style={{ fontSize: 12, color: 'var(--text-3)', margin: 0 }}>Dati non disponibili.</p>
          )}
        </BSDCard>

        {/* Card 5 — Persone in lista d'attesa */}
        <BSDCard
          kind="kpi"
          sezione={sezione}
          label="Persone in lista d'attesa"
          value={stats.totPersoneInLista.toLocaleString('it-IT')}
          icon={Clock}
          color={accentColor}
        />

      </AutoGrid>

      {/* ── Seconda riga ── */}
      <AutoGrid>

        {/* Card 6 — Strutture con lista aperta */}
        <BSDCard
          kind="kpi"
          sezione={sezione}
          label="Con lista d'attesa aperta"
          value={`${stats.conListaAperta.length} / ${filtered.length}`}
          icon={AlertTriangle}
          color="#f59e0b"
          sub={
            <div style={{ display: 'grid', gap: 4 }}>
              {stats.conListaAperta.map(s => (
                <div
                  key={s.id}
                  onClick={() => setSelectedStruttura(s)}
                  style={{ fontSize: 10, color: 'var(--text-2)', borderLeft: '2px solid #f59e0b', paddingLeft: 6, cursor: 'pointer', lineHeight: 1.4 }}
                >
                  {s.nome}
                </div>
              ))}
            </div>
          }
        />

        {/* Card 7 — Strutture con posti disponibili */}
        <BSDCard
          kind="kpi"
          sezione={sezione}
          label="Con posti disponibili"
          value={String(stats.conPostiLiberi.length)}
          icon={stats.conPostiLiberi.length > 0 ? CheckCircle : AlertTriangle}
          color={stats.conPostiLiberi.length > 0 ? '#10b981' : '#ef4444'}
          sub={
            stats.conPostiLiberi.length > 0 ? (
              <div style={{ display: 'grid', gap: 4 }}>
                {stats.conPostiLiberi.map(s => (
                  <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 4 }}>
                    <span
                      onClick={() => setSelectedStruttura(s)}
                      style={{ fontSize: 10, color: 'var(--text-2)', cursor: 'pointer', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >{s.nome}</span>
                    <span style={{ fontSize: 10, fontWeight: 700, color: '#10b981', flexShrink: 0 }}>{s.postiDisponibili}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ fontSize: 10, color: 'var(--text-3)', margin: 0 }}>Nessun posto disponibile.</p>
            )
          }
        />

        {/* Card 8 — Totale posti a contratto */}
        <BSDCard
          kind="kpi"
          sezione={sezione}
          label="Posti a contratto"
          value={stats.totPostiContratto.toLocaleString('it-IT')}
          icon={Layers}
          color="#6366f1"
        />

        {/* Card 9 — Posti disponibili totali */}
        <BSDCard
          kind="kpi"
          sezione={sezione}
          label="Posti disponibili"
          value={stats.totPostiDisponibili.toLocaleString('it-IT')}
          icon={BedDouble}
          color="#0ea5e9"
          sub={
            stats.totPostiDisponibili > 0 ? (
              <div style={{ display: 'grid', gap: 4 }}>
                {filtered.filter(s => s.postiDisponibili > 0).map(s => (
                  <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 4 }}>
                    <span
                      onClick={() => setSelectedStruttura(s)}
                      style={{ fontSize: 10, color: 'var(--text-2)', cursor: 'pointer', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >{s.nome}</span>
                    <span style={{ fontSize: 10, fontWeight: 700, color: '#0ea5e9', flexShrink: 0 }}>{s.postiDisponibili}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ fontSize: 10, color: 'var(--text-3)', margin: 0 }}>Nessun posto disponibile.</p>
            )
          }
        />

        {/* Card 10 — Spacer */}
        <BSDCard kind="panel" sezione={sezione} style={{ opacity: 0, pointerEvents: 'none' }} />

      </AutoGrid>

      <DisabilitaDialog
        struttura={selectedStruttura}
        tipo={tipo}
        onClose={() => setSelectedStruttura(null)}
        fromUrl={`/${tipo.toLowerCase()}`}
      />
      </div>
    </section>
  )
}
